var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useContext } from 'react';
import { Alert, Link, LoadingButton, ModalBody, ModalFooter, TextField, Typography, FormController, FormLabel, Input, FormStatusMessage, Checkbox, } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { FormProvider, useForm, Controller, } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { companyDetailsValidation } from '../validation';
import { accountApi } from '../../../../api/common/account';
import { addressesApi } from '../../../../api/common/addresses';
import { CREATE_PRO_LINKS } from '../../../../constants/links';
import { ZIPCODE_LENGTH } from '../../../../constants/general';
import { ERRORS_CODE, ERR_INVALID_ZIP, ERR_UNABLE_TO_SAVE, } from '../../../../constants/response-error-messages';
import { PHONE_PLACEHOLDER } from '../../../../constants/placeholders';
import { CityStateSelect, formatCityState, } from '../../../shared/city-state-select/city-state-select';
import { BusinessTypeSelect } from '../../../company-details/business-type-select';
import { getHyphenatedPhoneNumber } from '../../../../utils/general';
import { pushErrorEvent, setOverlayEvent } from '../../../../utils/analytics';
import { getTextFieldStatus } from '../../../../utils/form';
import { InfoPopover } from '../../../shared/popover/popover';
import { CONVERT_TO_PRO_PHONE_NUMBER_FIELD_POPOVER_MESSAGE, CONVERT_TO_PRO_PHONE_NUMBER_FIELD_POPOVER_TITLE, } from '../../../../constants/popover';
import { PHONE_NUMBER_ALREADY_REGISTERED_ERROR } from '../../../../constants/field-error-messages';
export var CompanyDetailsModalContent = function (_a) {
    var onSubmit = _a.onSubmit, defaultFirstName = _a.defaultFirstName, defaultLastName = _a.defaultLastName, defaultPhone = _a.defaultPhone;
    var _b = useState(null), tradeTypes = _b[0], setTradeTypes = _b[1];
    var _c = useState(false), loading = _c[0], setLoading = _c[1];
    var _d = useState(null), cities = _d[0], setCities = _d[1];
    var _e = useState(null), submitError = _e[0], setSubmitError = _e[1];
    var channel = useContext(ExperienceContext).channel;
    var isMobile = channel === 'mobile';
    var iconSize = isMobile ? 'small' : 'regular';
    var _f = useState(false), acknowledgeConversionRisk = _f[0], setAcknowledgeConversionRisk = _f[1];
    var defaultValues = {
        companyName: '',
        tradeType: '',
        otherBusiness: '',
        firstName: defaultFirstName || '',
        lastName: defaultLastName || '',
        phoneNumber: getHyphenatedPhoneNumber(defaultPhone),
        companyAddress: '',
        companyAddressLine2: '',
        cityState: '',
        zipCode: '',
    };
    var formMethods = useForm({
        resolver: yupResolver(companyDetailsValidation),
        defaultValues: defaultValues,
        mode: 'onChange',
        delayError: 500,
    });
    var control = formMethods.control, formErrors = formMethods.formState.errors, handleSubmit = formMethods.handleSubmit, setValue = formMethods.setValue, setError = formMethods.setError, trigger = formMethods.trigger, watch = formMethods.watch;
    var tradeType = watch().tradeType;
    useEffect(function () {
        setOverlayEvent('create pro xtra loyalty account');
        accountApi
            .getTradeTypes(false)
            .then(function (_a) {
            var data = _a.data;
            return setTradeTypes((data === null || data === void 0 ? void 0 : data.tradeTypes) || null);
        });
    }, []);
    var onZipCodeChange = function (event) {
        var _a = event.target, _b = _a === void 0 ? {} : _a, value = _b.value;
        trigger('zipCode').then(function (valid) {
            if (valid && value) {
                addressesApi
                    .getPostalList(value)
                    .then(function (data) {
                    var _a, _b;
                    setCities(data);
                    setValue('cityState', formatCityState((_a = data[0]) === null || _a === void 0 ? void 0 : _a.city, (_b = data[0]) === null || _b === void 0 ? void 0 : _b.state));
                })
                    .catch(function () {
                    setError('zipCode', ERR_INVALID_ZIP);
                    setValue('cityState', '');
                });
            }
        });
    };
    var handleChangeCheckbox = function () {
        setAcknowledgeConversionRisk(function (prevState) { return !prevState; });
    };
    var onChangePhone = function (event, 
    // if multiple phone inputs exist we can expand (contact vs company)
    inputId) {
        setValue(inputId, getHyphenatedPhoneNumber(event.currentTarget.value || ''));
        trigger(inputId);
    };
    var onSubmitForm = function (values) {
        setLoading(true);
        onSubmit(values)
            .catch(function (error) {
            var errorMsg;
            if (error === ERRORS_CODE.ERR_PHONE) {
                errorMsg = PHONE_NUMBER_ALREADY_REGISTERED_ERROR.message;
                setError('phoneNumber', __assign(__assign({}, PHONE_NUMBER_ALREADY_REGISTERED_ERROR), { type: 'custom' }));
            }
            else {
                errorMsg = ERR_UNABLE_TO_SAVE.message;
                setSubmitError(ERR_UNABLE_TO_SAVE);
            }
            pushErrorEvent('convert to pro modal', 'inline', 'n/a', errorMsg);
        })
            .finally(function () {
            setLoading(false);
        });
    };
    return (React.createElement(FormProvider, __assign({}, formMethods),
        React.createElement("form", { onSubmit: handleSubmit(onSubmitForm) },
            React.createElement(ModalBody, null,
                React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-3" },
                    React.createElement("img", { className: "sui-mr-3", alt: "pro xtra logo", height: "auto", width: "150", src: "https://assets.thdstatic.com/images/v1/pro-xtra.svg" }),
                    React.createElement(Typography, { variant: "body-lg", component: "div" },
                        React.createElement(Typography, { weight: "bold" }, "Fill out the information below."),
                        React.createElement(Typography, null, "Change any existing account details to match your business.")),
                    React.createElement(Controller, { name: "companyName", control: control, render: function (_a) {
                            var _b, _c;
                            var field = _a.field;
                            var ref = field.ref, rest = __rest(field, ["ref"]);
                            return (React.createElement(TextField, { required: true, fullWidth: true, label: "Company Name", status: ((_b = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _b === void 0 ? void 0 : _b.message) ? 'error' : undefined, statusMessage: (_c = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _c === void 0 ? void 0 : _c.message, InputProps: __assign({ inputProps: {
                                        maxLength: 60,
                                        'data-testid': 'step-company-name-inpt',
                                    } }, rest) }));
                        } }),
                    React.createElement(Controller, { control: control, name: "tradeType", render: function (_a) {
                            var _b;
                            var field = _a.field;
                            var ref = field.ref, rest = __rest(field, ["ref"]);
                            return (React.createElement(BusinessTypeSelect, __assign({ "data-testid": "businessType", tradeTypes: tradeTypes, placeholder: "Select Business Type", errorHelperText: (_b = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _b === void 0 ? void 0 : _b.message }, rest)));
                        } }),
                    tradeType === 'OTHR' && (React.createElement(Controller, { name: "otherBusiness", control: control, render: function (_a) {
                            var _b, _c;
                            var field = _a.field;
                            var ref = field.ref, rest = __rest(field, ["ref"]);
                            return (React.createElement(TextField, { optional: true, fullWidth: true, label: "Other Business Type", status: ((_b = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _b === void 0 ? void 0 : _b.message) ? 'error' : undefined, statusMessage: (_c = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _c === void 0 ? void 0 : _c.message, InputProps: __assign({ inputProps: { maxLength: 60 } }, rest) }));
                        } })),
                    React.createElement("div", { className: "sui-grid md:sui-grid-cols-2 sui-gap-4" },
                        React.createElement(Controller, { name: "firstName", control: control, render: function (_a) {
                                var _b, _c;
                                var field = _a.field;
                                var ref = field.ref, rest = __rest(field, ["ref"]);
                                return (React.createElement(TextField, { required: true, fullWidth: true, label: "First Name", status: ((_b = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _b === void 0 ? void 0 : _b.message) ? 'error' : undefined, statusMessage: (_c = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _c === void 0 ? void 0 : _c.message, InputProps: __assign({ inputProps: {
                                            maxLength: 40,
                                            'data-qm-mask': '',
                                            'data-testid': 'step-first-name-inpt',
                                        } }, rest) }));
                            } }),
                        React.createElement(Controller, { name: "lastName", control: control, render: function (_a) {
                                var _b, _c;
                                var field = _a.field;
                                var ref = field.ref, rest = __rest(field, ["ref"]);
                                return (React.createElement(TextField, { required: true, fullWidth: true, label: "Last Name", status: ((_b = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _b === void 0 ? void 0 : _b.message) ? 'error' : undefined, statusMessage: (_c = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _c === void 0 ? void 0 : _c.message, InputProps: __assign({ inputProps: {
                                            maxLength: 60,
                                            'data-qm-mask': '',
                                            'data-testid': 'step-last-name-inpt',
                                        } }, rest) }));
                            } })),
                    React.createElement(Controller, { name: "companyAddress", control: control, render: function (_a) {
                            var _b, _c;
                            var field = _a.field;
                            var ref = field.ref, rest = __rest(field, ["ref"]);
                            return (React.createElement(TextField, { required: true, fullWidth: true, label: "Company Address", status: ((_b = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _b === void 0 ? void 0 : _b.message) ? 'error' : undefined, statusMessage: (_c = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _c === void 0 ? void 0 : _c.message, InputProps: __assign(__assign({}, rest), { 'data-qm-mask': '', inputProps: {
                                        'data-testid': 'step-company-address-inpt',
                                    } }) }));
                        } }),
                    React.createElement(Controller, { name: "companyAddressLine2", control: control, render: function (_a) {
                            var _b, _c;
                            var field = _a.field;
                            var ref = field.ref, rest = __rest(field, ["ref"]);
                            return (React.createElement(TextField, { fullWidth: true, optional: true, label: "Address Line 2", status: ((_b = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _b === void 0 ? void 0 : _b.message) ? 'error' : undefined, statusMessage: (_c = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _c === void 0 ? void 0 : _c.message, InputProps: __assign(__assign({}, rest), { 'data-qm-mask': '', inputProps: {
                                        'data-testid': 'step-company-address-line2-inpt',
                                    } }) }));
                        } }),
                    React.createElement("div", { className: "sui-grid sm:sui-grid-cols-2 sui-gap-4" },
                        React.createElement(Controller, { name: "zipCode", control: control, render: function (_a) {
                                var _b, _c;
                                var field = _a.field;
                                var ref = field.ref, onChange = field.onChange, rest = __rest(field, ["ref", "onChange"]);
                                var onChangeWrapped = function (event) {
                                    var value = event.target.value;
                                    var onlyDigits = /^\d*$/;
                                    if (onlyDigits.test(value)) {
                                        onChange(event);
                                        onZipCodeChange(event);
                                    }
                                };
                                return (React.createElement(TextField, { required: true, fullWidth: true, label: "ZIP Code", status: ((_b = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _b === void 0 ? void 0 : _b.message) ? 'error' : undefined, statusMessage: (_c = formErrors === null || formErrors === void 0 ? void 0 : formErrors[field.name]) === null || _c === void 0 ? void 0 : _c.message, InputProps: __assign({ inputProps: {
                                            maxLength: ZIPCODE_LENGTH,
                                            'data-qm-mask': '',
                                            'data-testid': 'step-zip-code-inpt',
                                        }, onChange: onChangeWrapped }, rest) }));
                            } }),
                        React.createElement(Controller, { control: control, name: "cityState", render: function (_a) {
                                var field = _a.field;
                                var ref = field.ref, rest = __rest(field, ["ref"]);
                                return (React.createElement(CityStateSelect, __assign({ cities: cities }, rest, { "data-testid": "step-city-state-select" })));
                            } })),
                    React.createElement(Controller, { name: "phoneNumber", control: control, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            var ref = field.ref, onChange = field.onChange, rest = __rest(field, ["ref", "onChange"]);
                            return (React.createElement(FormController, null,
                                React.createElement(FormLabel, { htmlFor: "phoneNumber" },
                                    "Phone Number",
                                    React.createElement(InfoPopover, { iconsize: iconSize, message: CONVERT_TO_PRO_PHONE_NUMBER_FIELD_POPOVER_MESSAGE, title: CONVERT_TO_PRO_PHONE_NUMBER_FIELD_POPOVER_TITLE, placement: isMobile ? 'bottom' : 'right' })),
                                React.createElement(Input, __assign({}, rest, { "data-qm-mask": "", id: "phoneNumber", fullWidth: true, required: true, type: "text", status: getTextFieldStatus(formErrors.phoneNumber), onChange: function (event) {
                                        onChange(event);
                                        onChangePhone(event, field.name);
                                    }, autoComplete: "tel-national", placeholder: PHONE_PLACEHOLDER, inputProps: {
                                        'data-testid': 'step-phone-number-inpt',
                                    } })),
                                React.createElement(FormStatusMessage, { status: "error" }, (_b = formErrors.phoneNumber) === null || _b === void 0 ? void 0 : _b.message)));
                        } }),
                    React.createElement("div", { className: "sui-flex sui-flex-cols-2 sui-gap-1 sui-mb-4 sui-mt-2" },
                        React.createElement("div", { className: "-sui-ml-2" },
                            React.createElement(Checkbox, { checked: acknowledgeConversionRisk, onChange: handleChangeCheckbox, id: "acknowledgeConversionRisk", "data-testid": "step-acknowledge-conversion-risk-chk" })),
                        React.createElement("div", { className: "sui-pt-1" },
                            React.createElement("label", { htmlFor: "acknowledgeConversionRisk" },
                                React.createElement(Typography, { variant: "body-base" }, "I understand that once I convert to a Pro Xtra business account, the following apply:")),
                            React.createElement("ul", { className: "sui-list-disc sui-ml-6 sui-pt-2" },
                                React.createElement("li", null,
                                    React.createElement(Typography, { variant: "body-base" }, "I can\u2019t switch back to a personal account.")),
                                React.createElement("li", null,
                                    React.createElement(Typography, { variant: "body-base" }, "My purchase history may not transfer to my new account for at least 48 hours."))))),
                    submitError && React.createElement(Alert, { status: "error" }, submitError.message),
                    React.createElement(LoadingButton, { fullWidth: true, variant: "primary", type: "submit", "data-testid": "convert-to-pro-btn", loading: loading, disabled: !acknowledgeConversionRisk }, "Convert to Pro Xtra Account"))),
            React.createElement(ModalFooter, null,
                React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-2 sui-items-center" }, CREATE_PRO_LINKS.map(function (_a) {
                    var url = _a.url, text = _a.text;
                    return (React.createElement(Link, { key: url, href: url, target: "_blank" }, text));
                }))))));
};
